/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument, react-hooks/exhaustive-deps, @typescript-eslint/no-explicit-any, @typescript-eslint/no-misused-promises, @typescript-eslint/strict-boolean-expressions */

import { alpha, useTheme } from '@mui/material/styles';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Line,
  Tooltip,
  Legend,
  Brush,
  ComposedChart,
  Area,
  ReferenceLine,
  Bar,
  Cell,
  Rectangle,
} from 'recharts';
import { ErrorContent, Loader } from '../../shared';
import { DEFAULT_CHART_MARGINS, DEFAULT_Y_AXIS_STYLES } from 'config';
import { currentStatsState } from 'states/iVol';
import {
  dayjs,
  STRIKE_TICK_CONFIG,
  findNearestIdx,
  formatAsCurrency,
  formatAsPercentage,
  getQueryDate,
  predicateSearch,
  updateBrushZoomConfig,
} from 'util/shared';
import poll from 'util/poll';
import {
  getZoomConfigRefArea,
  DELTA_TICK_CONFIG,
  MONEYNESS_TICK_CONFIG,
  getTicksBrushed,
} from 'util/shared/chart';
import {
  DatedGreeks,
  PersistedVolSkewLine,
  RawGreeksDataMap,
  VolSkew,
  StrikeDisplayType,
  RawStatsData,
  VolSkewReferenceLine,
} from 'types/impliedVol';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import useExerciseStyles from 'hooks/iVol/useExerciseStyles';
import {
  DELTA_KEY,
  SKEW_TICK_FORMATTERS,
  VSKEW_DEFAULT_DTE,
  extractVolSkewData,
  findClosestExpiration,
  getFilteredGreeksData,
  getGroupedPayloads,
  getNextAvailableColor,
  getSnapshotTime,
  getStatsFromExp,
  getVolSkewTimeframeString,
  getVolSkewTimeframeStringFromLabel,
  mergeObjectLists,
  normDelta,
  getDeltaFromX,
  getDeltaX,
  getVolSkewLineLabel,
  getReferenceLineKey,
  convertToDaysTillExpiryIntraday,
  findD50Price,
  findClosestGreeksExp,
  getPricesPerTradeDateMap,
} from 'util/iVol';
import { workerState } from 'states/shared';
import useBrushZoom from 'hooks/useBrushZoom';
import ChartWatermarkContainer from 'components/shared/ChartWatermarkContainer';
import { pricesState, timezoneState, userDashAccessState } from 'states';
import { Paper, Typography, Stack } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import { DashAccess, hasDashAccess, ProductType, VolSkewKey } from 'types';
import useAuth from 'hooks/auth/useAuth';
import { IVAdjustmentSettings } from 'types/optionsCalculator';
import { getAdjustedIV } from 'util/optionsCalculator';
import useClosingPrices from 'hooks/equityhub/useClosingPrices';
import { useVolSkewCtx } from './VolSkewContext';
import {
  getSessionDay,
  hydrateLine,
  isSessionDay,
  sameLines,
  serializeLine,
} from './volSkewLines';
import { useDividends } from 'hooks/useDividends';
import { transformDividendsToRiskEngineFormat } from 'util/dividendUtils';

const DEFAULT_DELTA_TICKS = [
  0.01, 0.02, 0.04, 0.05, 0.1, 0.25, 0.5, 0.75, 0.9, 0.95, 0.9, 0.95, 0.96,
  0.97, 0.98, 0.99,
];

const SKEW_STRIKE_TICK_CONFIG: Map<number, number> = new Map([
  [1_200, 200],
  ...STRIKE_TICK_CONFIG,
  [10, 2],
  [1, 1],
]);

const SKEW_KEY_TO_DISPLAY = {
  delta: StrikeDisplayType.Delta,
  'delta-x': StrikeDisplayType.Delta,
  moneyness: StrikeDisplayType.Moneyness,
  strike: StrikeDisplayType.FixedStrike,
};

const SKEW_DIFF_KEY = 'skewDiff';

const formatSkewDiff = (value: number): string =>
  `${value > 0 ? '+' : ''}${value.toFixed(2)} vol pts`;

interface VolSkewChartProps {
  defaultExpiration?: number;
  ivAdjustmentParams?: IVAdjustmentSettings;
  referenceLine?: VolSkewReferenceLine;
  forceReset?: boolean;
  bypassAccessCheck?: boolean;
  // Extra lines rendered from caller-supplied data (DatedGreeks.precomputedVolSkew
  // set) instead of the normal fetch pipeline - not persisted to config.lines,
  // supplied fresh by the caller on every render.
  precomputedLines?: DatedGreeks[];
}

interface SkewDiffComparison {
  baseLine: DatedGreeks;
  comparisonLine: DatedGreeks;
  baseLabel: string;
  comparisonLabel: string;
  diffLabel: string;
  data: VolSkew[];
  domain: [number, number];
}

interface SkewDiffSeriesEntry {
  x: number;
  value: number;
}

type BrushIndices = {
  startIndex?: number;
  endIndex?: number;
};

const hasSkewExpiry = (datedGreeks: DatedGreeks): boolean =>
  datedGreeks.expiryDate != null && datedGreeks.expiryDate !== '';

const compareSkewSelections = (a: DatedGreeks, b: DatedGreeks): number => {
  const tradeDateDiff = a.tradeDate.valueOf() - b.tradeDate.valueOf();
  if (tradeDateDiff !== 0) {
    return tradeDateDiff;
  }

  const expiryDiff = Number(a.expiryDate) - Number(b.expiryDate);
  if (Number.isFinite(expiryDiff) && expiryDiff !== 0) {
    return expiryDiff;
  }

  return a.key.localeCompare(b.key);
};

const getSkewDiffSeriesX = (
  point: VolSkew,
  xKey: VolSkewKey,
  lineKey: string,
): number => {
  if (xKey === 'delta-x') {
    const lineDelta = point[`${lineKey}-${DELTA_KEY}`];
    if (Number.isFinite(lineDelta)) {
      return getDeltaX(lineDelta);
    }
  }

  const sharedX = point[xKey];
  return Number.isFinite(sharedX) ? (sharedX as number) : Number.NaN;
};

const buildSkewDiffSeries = (
  data: VolSkew[],
  xKey: VolSkewKey,
  lineKey: string,
): SkewDiffSeriesEntry[] =>
  data
    .map((point) => ({
      x: getSkewDiffSeriesX(point, xKey, lineKey),
      value: point[lineKey],
    }))
    .filter(
      (point): point is SkewDiffSeriesEntry =>
        Number.isFinite(point.x) && Number.isFinite(point.value),
    )
    .sort((a, b) => a.x - b.x);

const interpolateSkewDiffValue = (
  series: SkewDiffSeriesEntry[],
  x: number,
): number | null => {
  const leftIdx = predicateSearch(series, (point) => point.x <= x);
  if (leftIdx < 0) {
    return null;
  }

  const left = series[leftIdx];
  if (left.x === x) {
    return left.value;
  }

  const right = series[leftIdx + 1];
  if (right == null) {
    return null;
  }

  const fraction = (x - left.x) / (right.x - left.x);
  return left.value + (right.value - left.value) * fraction;
};

const buildSkewDiffXValues = (
  baseSeries: SkewDiffSeriesEntry[],
  comparisonSeries: SkewDiffSeriesEntry[],
): number[] =>
  Array.from(
    new Set([...baseSeries, ...comparisonSeries].map((point) => point.x)),
  ).sort((a, b) => a - b);

const VolSkewChart = ({
  defaultExpiration,
  ivAdjustmentParams,
  referenceLine,
  forceReset = false,
  bypassAccessCheck = false,
  precomputedLines = [],
}: VolSkewChartProps) => {
  const {
    selectedSym,
    config,
    updateConfig: onConfigChange,
    selectedDatedGreeks: volSkewSelectedDatedGreeks,
    setSelectedDatedGreeks: setVolSkewSelectedDatedGreeks,
    visibleChartLines: volSkewVisibleChartLines,
    setVisibleChartLines,
    zoomConfig,
    setZoomConfig,
    volSkewData,
    setVolSkewData,
    chartData: volSkewChartData,
    setChartData: setVolSkewChartData,
    loading,
    setLoading,
    error,
    setError,
    chartRef,
  } = useVolSkewCtx();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const currentTimezone = useRecoilValue(timezoneState);
  const worker = useRecoilValue(workerState);
  const { hasAccessToProduct } = useAuth();
  const hasIVolAccess = hasAccessToProduct(ProductType.IMPLIED_VOL);
  const { getCurrentGreeksData, getDailyGreeksData, getStatisticsData } =
    useImpliedVolatility({ bypassAccessCheck });
  const exerciseStyles = useExerciseStyles(selectedSym);

  const dashAccess = useRecoilValue(userDashAccessState);
  const useSpotGammaIV = config.useSpotGammaIV;
  const selectedDisplayType = config.strikeDisplay;
  const showVolSkewIVStats = config.showStats;
  const showSkewDiff = config.showSkewDiff;
  const showZoomBrush = config.showZoomBrush;
  const markedStrikes = config.markedStrikes;
  // Legs at the same strike collapse to one marker; disagreeing colors use warning.
  const markedStrikeGroups = useMemo(() => {
    const byStrike = new Map<
      number,
      { label: string; color?: string; disagreement: boolean }
    >();
    markedStrikes.forEach(({ strike, label, color }) => {
      const existing = byStrike.get(strike);
      if (!existing) {
        byStrike.set(strike, { label, color, disagreement: false });
      } else {
        existing.label = `${existing.label} / ${label}`;
        if (existing.color !== color) {
          existing.disagreement = true;
        }
      }
    });
    return Array.from(byStrike.entries()).map(
      ([strike, { label, color, disagreement }]) => ({
        strike,
        label,
        color: disagreement ? theme.palette.warning.main : color,
      }),
    );
  }, [markedStrikes, theme]);
  const canShowSkewDiff = hasDashAccess(dashAccess, DashAccess.BETA);
  const currentStatsLookback = config.statsLookback;

  const availableColors = Object.values(theme.palette.iVol.volSkew);

  const { getClosingPrice } = useClosingPrices();

  const { data: dividendsData } = useDividends({ sym: selectedSym });
  const { dividendDates, dividendAmounts } = useMemo(
    () => transformDividendsToRiskEngineFormat(dividendsData ?? []),
    [dividendsData],
  );

  const [prices] = useRecoilState(pricesState);
  const [closingPrice, setClosingPrice] = useState<number | null>(
    prices.get(selectedSym) ?? null,
  );
  const [shouldUpdateChart, setShouldUpdateChart] = useState<boolean>(false);

  const setCurrentStats = useSetRecoilState(currentStatsState);

  const volSkewSharedKey: VolSkewKey = useMemo(
    () => getVolSkewTimeframeStringFromLabel(selectedDisplayType),
    [selectedDisplayType],
  );

  const handleResponse = useCallback(
    async (response: { data: RawGreeksDataMap }) => {
      if (response.data != null) {
        const filteredNewGreeks = getFilteredGreeksData(response.data);

        const [updatedClosingPrice, updatedClosingPriceMap] = await Promise.all(
          [
            getClosingPrice(selectedSym),
            getPricesPerTradeDateMap(
              volSkewSelectedDatedGreeks.map((dg) => dg.tradeDate),
              selectedSym,
              getClosingPrice,
            ),
          ],
        );

        setVolSkewSelectedDatedGreeks((prevGreeks: DatedGreeks[]) => {
          return prevGreeks.map((datedGreeks: DatedGreeks) => {
            // Only the current-session line(s) take live polled greeks;
            // historical comparison lines stay frozen at their snapshot.
            if (!isSessionDay(datedGreeks.tradeDate, currentTimezone)) {
              return datedGreeks;
            }
            const updatedClosingPrice = updatedClosingPriceMap?.get(
              datedGreeks.tradeDate.format('YYYY-MM-DD'),
            );
            return {
              ...datedGreeks,
              currentPrice: updatedClosingPrice ?? datedGreeks.currentPrice,
              greeksMap: filteredNewGreeks ?? datedGreeks.greeksMap,
            };
          });
        });

        if (updatedClosingPrice != null) {
          setClosingPrice(updatedClosingPrice);
        }

        setShouldUpdateChart(true);
      }
    },

    [
      setVolSkewSelectedDatedGreeks,
      selectedSym,
      dashAccess,
      useSpotGammaIV,
      currentTimezone,
      getClosingPrice,
    ],
  );

  useEffect(() => {
    if (volSkewSelectedDatedGreeks.length === 0 || !shouldUpdateChart) {
      return;
    }

    let finalData: VolSkew[] = [];

    volSkewSelectedDatedGreeks.forEach((datedGreeks: DatedGreeks) => {
      if (!volSkewVisibleChartLines.includes(datedGreeks.key)) {
        return;
      }

      // Precomputed lines have no greeksMap to re-extract from - a live
      // greeks update should leave them exactly as the caller supplied them.
      if (datedGreeks.precomputedVolSkew != null) {
        finalData = mergeObjectLists(
          finalData,
          datedGreeks.precomputedVolSkew,
          volSkewSharedKey,
        );
        return;
      }

      const updatedVolSkewData: VolSkew[] = extractVolSkewData(
        datedGreeks.greeksMap[Number(datedGreeks.expiryDate)],
        datedGreeks.key,
        datedGreeks.statsMap,
        datedGreeks.currentPrice,
        hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
        datedGreeks.tradeDate,
        dayjs(Number(datedGreeks.expiryDate)),
        dividendDates,
        dividendAmounts,
        exerciseStyles,
      );

      finalData = mergeObjectLists(
        finalData,
        updatedVolSkewData,
        volSkewSharedKey,
      );
    });

    setVolSkewData(finalData);
    setShouldUpdateChart(false);
  }, [
    volSkewSelectedDatedGreeks,
    volSkewVisibleChartLines,
    shouldUpdateChart,
    dashAccess,
    useSpotGammaIV,
    dividendDates,
    dividendAmounts,
    exerciseStyles,
  ]);

  useEffect(() => {
    return poll(worker, {
      url: hasIVolAccess
        ? `v2/latest_greeks?sym=${encodeURIComponent(selectedSym)}`
        : `v2/free_latest_greeks?sym=${encodeURIComponent(selectedSym)}`,
      interval: 10_000,
      buffer: true,
      msgpack: true,
      onResponse: handleResponse,
    });
  }, [worker, hasIVolAccess, handleResponse, selectedSym]);

  // The default line: current greeks at the closest expiry to the target DTE
  // (the `defaultExpiration` prop's DTE if given, else 30).
  const seedDefaultLine = useCallback(async (): Promise<DatedGreeks | null> => {
    const greeks = await getCurrentGreeksData(selectedSym);
    if (greeks == null) {
      return null;
    }
    const snapTime = getSnapshotTime(greeks);
    const expirations = Object.keys(greeks);
    const targetDTE = defaultExpiration
      ? convertToDaysTillExpiryIntraday(
          getQueryDate(true, currentTimezone).valueOf(),
          Number(defaultExpiration),
        )
      : VSKEW_DEFAULT_DTE;
    const expiry = findClosestExpiration(snapTime, expirations, targetDTE);
    if (expiry == null) {
      return null;
    }
    const tradeDate = dayjs(snapTime);
    const fwdPrice = findD50Price(greeks[Number(expiry)]);
    const currentPrice =
      (await getClosingPrice(selectedSym, tradeDate.format('YYYY-MM-DD'))) ||
      fwdPrice;
    return {
      key: getVolSkewTimeframeString(tradeDate, expiry),
      sym: selectedSym,
      tradeDate,
      expiryDate: expiry,
      greeksMap: greeks,
      statsMap: {},
      currentPrice,
      color:
        getNextAvailableColor([], availableColors) ??
        theme.palette.primary.main,
    };
  }, [
    getCurrentGreeksData,
    selectedSym,
    defaultExpiration,
    currentTimezone,
    getClosingPrice,
    availableColors,
    theme,
  ]);

  // Mirror of the working set so the pipeline can reuse loaded greeks without
  // depending on `selectedDatedGreeks` (which it writes to).
  const linesRef = useRef<DatedGreeks[]>(volSkewSelectedDatedGreeks);
  linesRef.current = volSkewSelectedDatedGreeks;
  // The input signature the pipeline last ran for; re-running only on change
  // prevents config write-backs from looping (even on no-op-onConfigChange hosts).
  const lastRunSigRef = useRef<string | null>(null);

  const visibleSelectedDatedGreeks = useMemo(
    () =>
      volSkewSelectedDatedGreeks.filter((datedGreeks: DatedGreeks) =>
        volSkewVisibleChartLines.includes(datedGreeks.key),
      ),
    [volSkewSelectedDatedGreeks, volSkewVisibleChartLines],
  );

  // ── Single load pipeline ──────────────────────────────────────────────
  // The one place that decides which lines exist and loads their data: seeds
  // the default on a new day / empty / forceReset, else hydrates saved lines.
  const lineSig = (lines: PersistedVolSkewLine[], asOf?: string) =>
    JSON.stringify({
      sym: selectedSym,
      sgiv: useSpotGammaIV,
      def: defaultExpiration ?? null,
      ref: referenceLine ?? null,
      force: forceReset,
      asOf: asOf ?? null,
      lines,
      precomputed: precomputedLines,
      dd: dividendDates,
      da: dividendAmounts,
    });
  const runSig = lineSig(config.lines, config.asOfDate);

  useEffect(() => {
    // Run only when inputs actually changed (our own writes keep runSig stable).
    if (runSig === lastRunSigRef.current) {
      return;
    }
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const sessionDay = getSessionDay(currentTimezone);
        const needReset =
          forceReset ||
          config.asOfDate !== sessionDay ||
          config.lines.length === 0;

        // 1) Desired line specs. On reset → just the seeded default. Otherwise
        //    hydrate the saved lines, reusing in-memory greeks where present.
        let specs: DatedGreeks[];
        if (needReset) {
          if (config.lines.length === 0 && precomputedLines.length > 0) {
            // Caller is driving this chart entirely with precomputed data -
            // no default real line to seed, and a failed real-data fetch
            // below shouldn't block precomputed lines from rendering.
            specs = [];
          } else {
            const seed = await seedDefaultLine();
            if (cancelled) {
              return;
            }
            if (seed == null) {
              setError('Failed to load vol skew data');
              return;
            }
            specs = [seed];
          }
        } else {
          const byKey = new Map(linesRef.current.map((d) => [d.key, d]));
          specs = config.lines.map(
            (l) => byKey.get(l.key) ?? hydrateLine(l, selectedSym),
          );
        }

        // 2) Fetch stats + per-tradeDate prices once, and greeks for any spec
        //    that doesn't already carry them (current vs daily by session day).
        const [stats, closingPriceMap, ...greeksList] = await Promise.all([
          getStatisticsData(selectedSym),
          getPricesPerTradeDateMap(
            specs.map((s) => s.tradeDate),
            selectedSym,
            getClosingPrice,
          ),
          ...specs.map((s) =>
            Object.keys(s.greeksMap).length > 0
              ? Promise.resolve<RawGreeksDataMap | null>(s.greeksMap)
              : isSessionDay(s.tradeDate, currentTimezone)
                ? getCurrentGreeksData(selectedSym)
                : getDailyGreeksData(
                    s.tradeDate.format('YYYY-MM-DD'),
                    selectedSym,
                  ),
          ),
        ]);
        if (cancelled) {
          return;
        }
        if (stats == null && specs.length > 0) {
          setError('Failed to load vol skew data');
          return;
        }
        const statsData: RawStatsData = stats?.[currentStatsLookback] ?? {};
        const useSGIV =
          hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV;

        // 3) Resolve each spec: pick a valid expiry, build its chart series.
        let finalData: VolSkew[] = [];
        const resolved: DatedGreeks[] = [];
        const usedColors: string[] = [];

        specs.forEach((spec, index) => {
          const greeks = greeksList[index];
          if (greeks == null) {
            return;
          }
          const snapTime = getSnapshotTime(greeks);
          const expirations = Object.keys(greeks);
          const todayLike = isSessionDay(spec.tradeDate, currentTimezone);
          const targetDTE = defaultExpiration
            ? convertToDaysTillExpiryIntraday(
                getQueryDate(true, currentTimezone).valueOf(),
                Number(defaultExpiration),
              )
            : VSKEW_DEFAULT_DTE;
          const defExpiry = findClosestExpiration(
            snapTime,
            expirations,
            targetDTE,
          );

          let expiry = spec.expiryDate;
          const expiryMs = Number(expiry);
          if (!expiry || !Number.isFinite(expiryMs) || expiryMs <= 0) {
            expiry = defExpiry;
          } else if (greeks[Number(expiry)] == null) {
            // A saved expiry that no longer exists: today lines re-snap to the
            // closest target DTE; historical lines are dropped.
            if (!todayLike) {
              return;
            }
            expiry = defExpiry;
          }
          if (expiry == null) {
            return;
          }

          const key = getVolSkewTimeframeString(spec.tradeDate, expiry);
          const statsMap = getStatsFromExp(statsData, greeks, expiry);
          const fwdPrice = findD50Price(
            greeks[findClosestGreeksExp(greeks, spec.tradeDate)],
          );
          const currentPrice =
            closingPriceMap.get(spec.tradeDate.format('YYYY-MM-DD')) ||
            fwdPrice;
          const color =
            spec.color !== ''
              ? spec.color
              : (getNextAvailableColor(usedColors, availableColors) ??
                theme.palette.primary.main);
          usedColors.push(color);

          if (!config.hiddenKeys.includes(key)) {
            finalData = mergeObjectLists(
              finalData,
              extractVolSkewData(
                greeks[Number(expiry)],
                key,
                statsMap,
                currentPrice,
                useSGIV,
                spec.tradeDate,
                dayjs(Number(expiry)),
                dividendDates,
                dividendAmounts,
                exerciseStyles,
              ),
              volSkewSharedKey,
            );
          }

          resolved.push({
            key,
            sym: selectedSym,
            tradeDate: spec.tradeDate,
            expiryDate: expiry,
            greeksMap: greeks,
            statsMap,
            currentPrice,
            color,
          });
        });

        // 3b) Precomputed lines - caller-supplied data, never fetched, never
        // persisted (see the exclusion in step 4 below).
        precomputedLines.forEach((dg) => {
          const key = getVolSkewTimeframeString(dg.tradeDate, dg.expiryDate);
          const color =
            dg.color !== ''
              ? dg.color
              : (getNextAvailableColor(usedColors, availableColors) ??
                theme.palette.primary.main);
          usedColors.push(color);

          if (
            dg.precomputedVolSkew != null &&
            !config.hiddenKeys.includes(key)
          ) {
            finalData = mergeObjectLists(
              finalData,
              dg.precomputedVolSkew,
              volSkewSharedKey,
            );
          }

          resolved.push({ ...dg, key, color });
        });

        // Drop any lines that collapsed onto the same (tradeDate, expiry) key.
        const unique = resolved.filter(
          (d, i, arr) => arr.findIndex((x) => x.key === d.key) === i,
        );
        if (cancelled) {
          return;
        }

        setVolSkewSelectedDatedGreeks(unique);
        setVisibleChartLines(
          unique
            .map((d) => d.key)
            .filter((k) => !config.hiddenKeys.includes(k)),
        );
        setVolSkewData(finalData);
        // Shared across iVol components - don't clobber an already-fetched
        // value with null just because this precomputed-only render skipped
        // (or failed) its own stats fetch.
        if (stats != null) {
          setCurrentStats(stats);
        }
        const sessionClosing = closingPriceMap.get(sessionDay);
        if (sessionClosing != null) {
          setClosingPrice(sessionClosing);
        }

        // 4) Persist the resolved set — only when it actually changed.
        // Precomputed lines are supplied by the caller on every render, not
        // something to restore from persisted config - drop them here too.
        const persisted = unique
          .filter((d) => d.precomputedVolSkew == null)
          .map(serializeLine);
        const linesChanged = !sameLines(persisted, config.lines);
        const asOfChanged = config.asOfDate !== sessionDay;
        const hiddenNeedsClear = needReset && config.hiddenKeys.length > 0;
        if (linesChanged || asOfChanged || hiddenNeedsClear) {
          onConfigChange({
            lines: persisted,
            asOfDate: sessionDay,
            ...(needReset ? { hiddenKeys: [] } : {}),
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load vol skew data',
          );
        }
      } finally {
        // Mark as run only on a non-superseded completion (StrictMode-safe).
        if (!cancelled) {
          lastRunSigRef.current = runSig;
          setLoading(false);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [runSig, exerciseStyles]);

  const getIVAdjustedVolSkewData = (
    data: VolSkew[],
    adjustmentParams?: IVAdjustmentSettings,
  ) => {
    // Create modified data with IV adjustments if parameters are provided
    if (adjustmentParams != null) {
      return data.map((vs: VolSkew) => {
        // Create a copy with modified baseline values based on IV adjustment parameters
        const modifiedVs = { ...vs };

        // Apply IV adjustments to each visible dated greeks
        visibleSelectedDatedGreeks.forEach((dg: DatedGreeks) => {
          // Use the default current IV key for adjustments
          const currentIVKey = dg.key;
          const currentIVValue = vs[currentIVKey] as number;

          if (currentIVValue == null) {
            return;
          }
          // Find the minimum IV value for this timeframe across all vol skew data points
          const minIV = data.reduce((min, current) => {
            const currentValue = current[currentIVKey] as number;

            if (currentValue != null && (min == null || currentValue < min)) {
              return currentValue;
            }
            return min;
          }, data[0][currentIVKey] as number);

          // Find the strike with minimum IV for this timeframe
          const minIVSkewObject = data.reduce((minStrike, current) => {
            const currentValue = current[currentIVKey] as number;
            const minValue = minStrike
              ? (minStrike[currentIVKey] as number)
              : null;

            if (
              currentValue != null &&
              (minValue == null || currentValue < minValue)
            ) {
              return current;
            }
            return minStrike;
          }, data[0]);

          // Create adjusted key name
          const adjustedKey = `${currentIVKey}-adjusted`;
          modifiedVs[adjustedKey] = getAdjustedIV(
            currentIVValue,
            minIV,
            adjustmentParams,
            vs[volSkewSharedKey] > minIVSkewObject[volSkewSharedKey], // determine if this is a call or put?
          );
        });

        return modifiedVs;
      });
    }

    return data;
  };

  useEffect(() => {
    if (volSkewData) {
      let sortedData = volSkewData.slice();

      sortedData = sortedData
        .sort(
          (vs1: VolSkew, vs2: VolSkew) =>
            vs1[volSkewSharedKey] - vs2[volSkewSharedKey],
        )
        .filter((vs: VolSkew, index: number, self: VolSkew[]) => {
          const prev = self[index - 1]?.[volSkewSharedKey];
          return (
            prev !== vs[volSkewSharedKey] &&
            vs['delta-x'] >= 0.001 &&
            vs['delta-x'] <= 0.999
          );
        });

      const finalChartData = getIVAdjustedVolSkewData(
        sortedData,
        ivAdjustmentParams,
      );

      setVolSkewChartData(finalChartData);
      updateBrushZoomConfig(zoomConfig, finalChartData, setZoomConfig);
    }
  }, [
    setVolSkewChartData,
    setZoomConfig,
    volSkewData,
    volSkewSharedKey,
    ivAdjustmentParams,
  ]);

  const skewDiffComparison = useMemo<SkewDiffComparison | null>(() => {
    if (
      !canShowSkewDiff ||
      !showSkewDiff ||
      visibleSelectedDatedGreeks.length !== 2
    ) {
      return null;
    }

    const [firstLine, secondLine] = visibleSelectedDatedGreeks;
    if (
      !hasSkewExpiry(firstLine) ||
      !hasSkewExpiry(secondLine) ||
      // Precomputed lines have no greeksMap to diff from - skip the
      // comparison rather than extracting from an empty map.
      firstLine.precomputedVolSkew != null ||
      secondLine.precomputedVolSkew != null
    ) {
      return null;
    }

    const [baseLine, comparisonLine] = [firstLine, secondLine].sort(
      compareSkewSelections,
    );
    const shouldUseSpotGammaIV =
      hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV;
    const baseLineData = extractVolSkewData(
      baseLine.greeksMap[Number(baseLine.expiryDate)],
      baseLine.key,
      baseLine.statsMap,
      baseLine.currentPrice,
      shouldUseSpotGammaIV,
      baseLine.tradeDate,
      dayjs(Number(baseLine.expiryDate)),
      dividendDates,
      dividendAmounts,
      exerciseStyles,
    );
    const comparisonLineData = extractVolSkewData(
      comparisonLine.greeksMap[Number(comparisonLine.expiryDate)],
      comparisonLine.key,
      comparisonLine.statsMap,
      comparisonLine.currentPrice,
      shouldUseSpotGammaIV,
      comparisonLine.tradeDate,
      dayjs(Number(comparisonLine.expiryDate)),
      dividendDates,
      dividendAmounts,
      exerciseStyles,
    );

    const baseSeries = buildSkewDiffSeries(
      baseLineData,
      volSkewSharedKey,
      baseLine.key,
    );
    const comparisonSeries = buildSkewDiffSeries(
      comparisonLineData,
      volSkewSharedKey,
      comparisonLine.key,
    );
    const xValues = buildSkewDiffXValues(baseSeries, comparisonSeries);

    let maxAbsChange = 0;
    const data = xValues.reduce<VolSkew[]>((skewDiffData, x) => {
      const baseValue = interpolateSkewDiffValue(baseSeries, x);
      const comparisonValue = interpolateSkewDiffValue(comparisonSeries, x);
      if (!Number.isFinite(baseValue) || !Number.isFinite(comparisonValue)) {
        return skewDiffData;
      }

      const baseVol = baseValue as number;
      const comparisonVol = comparisonValue as number;
      const skewDiff = (comparisonVol - baseVol) * 100;
      maxAbsChange = Math.max(maxAbsChange, Math.abs(skewDiff));

      skewDiffData.push({
        strike: x,
        moneyness: x,
        'delta-x': x,
        [volSkewSharedKey]: x,
        [baseLine.key]: baseVol,
        [comparisonLine.key]: comparisonVol,
        [SKEW_DIFF_KEY]: skewDiff,
      });

      return skewDiffData;
    }, []);

    if (data.length === 0) {
      return null;
    }

    const paddedMaxAbsChange = Math.max(maxAbsChange * 1.15, 0.5);
    const baseLabel =
      baseLine.legendLabel ??
      getVolSkewLineLabel(
        baseLine.tradeDate,
        baseLine.expiryDate,
        currentTimezone,
      );
    const comparisonLabel =
      comparisonLine.legendLabel ??
      getVolSkewLineLabel(
        comparisonLine.tradeDate,
        comparisonLine.expiryDate,
        currentTimezone,
      );

    return {
      baseLine,
      comparisonLine,
      baseLabel,
      comparisonLabel,
      diffLabel: `${comparisonLabel} minus ${baseLabel}`,
      data,
      domain: [-paddedMaxAbsChange, paddedMaxAbsChange],
    };
  }, [
    visibleSelectedDatedGreeks,
    canShowSkewDiff,
    showSkewDiff,
    volSkewSharedKey,
    currentTimezone,
    dashAccess,
    useSpotGammaIV,
    dividendDates,
    dividendAmounts,
    exerciseStyles,
  ]);

  const brushData = skewDiffComparison?.data ?? volSkewChartData;

  const { zoomChartConfig } = useBrushZoom<VolSkew>(
    zoomConfig,
    setZoomConfig,
    volSkewSharedKey,
    brushData,
  );

  const onBrushChange = useCallback(
    (brushIndices: BrushIndices) => {
      setZoomConfig((prev) => ({
        ...prev,
        leftIdx: brushIndices.startIndex,
        rightIdx: brushIndices.endIndex,
      }));
    },
    [setZoomConfig],
  );

  const lastBrushIdx = Math.max(brushData.length - 1, 0);
  const activeLeftBrushIdx = Math.min(zoomConfig.leftIdx ?? 0, lastBrushIdx);
  const activeRightBrushIdx = Math.min(
    zoomConfig.rightIdx ?? lastBrushIdx,
    lastBrushIdx,
  );
  const activeBrushZoomConfig = useMemo(
    () => ({
      ...zoomConfig,
      leftIdx: Math.min(activeLeftBrushIdx, activeRightBrushIdx),
      rightIdx: Math.max(activeLeftBrushIdx, activeRightBrushIdx),
    }),
    [activeLeftBrushIdx, activeRightBrushIdx, zoomConfig],
  );

  const displayedVolSkewChartData = useMemo(() => {
    if (skewDiffComparison == null) {
      return zoomChartConfig.data;
    }

    const leftX =
      skewDiffComparison.data[activeBrushZoomConfig.leftIdx]?.[
        volSkewSharedKey
      ];
    const rightX =
      skewDiffComparison.data[activeBrushZoomConfig.rightIdx]?.[
        volSkewSharedKey
      ];

    if (!Number.isFinite(leftX) || !Number.isFinite(rightX)) {
      return volSkewChartData;
    }

    const minX = Math.min(leftX, rightX);
    const maxX = Math.max(leftX, rightX);

    return volSkewChartData.filter((point) => {
      const x = point[volSkewSharedKey];
      return Number.isFinite(x) && x >= minX && x <= maxX;
    });
  }, [
    skewDiffComparison,
    volSkewChartData,
    volSkewSharedKey,
    zoomChartConfig.data,
    activeBrushZoomConfig.leftIdx,
    activeBrushZoomConfig.rightIdx,
  ]);

  const ticks = useMemo(() => {
    const values =
      volSkewSharedKey === 'delta-x'
        ? brushData.map((e: VolSkew) => normDelta(getDeltaFromX(e['delta-x'])))
        : brushData.map((volSkew: VolSkew) => volSkew[volSkewSharedKey]);
    let ticks = getTicksBrushed(
      values,
      activeBrushZoomConfig,
      selectedDisplayType === StrikeDisplayType.FixedStrike
        ? SKEW_STRIKE_TICK_CONFIG
        : selectedDisplayType === StrikeDisplayType.Moneyness
          ? MONEYNESS_TICK_CONFIG
          : DELTA_TICK_CONFIG,
    );
    if (volSkewSharedKey === 'delta-x') {
      // Manually add all rounded numbers at the wings if they are within our values range
      ticks = ticks ?? [...DEFAULT_DELTA_TICKS];
      const maxVal = values[activeBrushZoomConfig.leftIdx] ?? values[0]; // normed deltas are descending
      const minVal =
        values[activeBrushZoomConfig.rightIdx ?? values.length - 1] ??
        values[values.length - 1];
      let tick = (Math.floor(ticks[0] * 100) - 1) / 100;
      for (; tick > minVal; tick -= 0.01) {
        ticks.unshift(tick);
      }
      tick = (Math.floor(ticks[ticks.length - 1] * 100) + 1) / 100;
      for (; tick < maxVal; tick += 0.01) {
        ticks.push(tick);
      }
      return ticks.map(getDeltaX);
    }
    return ticks;
  }, [activeBrushZoomConfig, brushData, selectedDisplayType, volSkewSharedKey]);

  const tickFormatter = (key: VolSkewKey) => (value: number) =>
    SKEW_TICK_FORMATTERS[key](value);

  const customTooltipTickFormatter = (key: VolSkewKey, value: any) => {
    const keyLabel = SKEW_KEY_TO_DISPLAY[key];
    const valueLabel = SKEW_TICK_FORMATTERS[key](value);

    return (
      <Stack
        key={key}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        gap={1}
      >
        <Typography
          sx={{
            fontSize: '12px',
          }}
        >
          {keyLabel}
        </Typography>

        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {valueLabel}
        </Typography>
      </Stack>
    );
  };

  const chartTooltipShadow = `${theme.palette.background.paper} 1px 1px 4px, ${theme.palette.background.paper} 1px -1px 4px, ${theme.palette.background.paper} -1px 1px 4px, ${theme.palette.background.paper} -1px -1px 4px, ${theme.palette.background.paper} 2px 2px 4px, ${theme.palette.background.paper} 2px -2px 4px, ${theme.palette.background.paper} -2px 2px 4px, ${theme.palette.background.paper} 2px -2px 4px`;

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const firstPayloadObj = payload[0];
      const groupedPayloads = getGroupedPayloads('bounds-', payload);
      return (
        <Paper
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '12px',
            gap: '6px',
            color: theme.palette.text.primary,
            border: 'none',
            backgroundColor: alpha(theme.palette.background.paper, 0.85),
            boxShadow: theme.palette.shadows.paperBoxShadow,
            width: '300px',
          }}
        >
          <Stack>
            {Object.entries(firstPayloadObj.payload)
              .filter(([key]) => key === 'strike' || key === 'moneyness')
              .map(([key, value]: [string, any]) =>
                customTooltipTickFormatter(key as VolSkewKey, value),
              )}
          </Stack>

          {Object.entries(groupedPayloads).map(([key, items]) => (
            <Stack key={key}>
              {items.map((payloadObj) => {
                if (payloadObj.name.includes('Bounds')) {
                  return (
                    <Stack key={payloadObj.name} width="100%" gap={1}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        gap={1}
                      >
                        <Typography
                          sx={{
                            color: payloadObj.color,
                            fontSize: '13px',
                            textShadow: chartTooltipShadow,
                          }}
                        >
                          Delta
                        </Typography>
                        <Typography
                          sx={{
                            color: payloadObj.color,
                            fontSize: '13px',
                            fontWeight: 'bold',
                            textShadow: chartTooltipShadow,
                          }}
                        >
                          {SKEW_TICK_FORMATTERS['delta'](
                            payloadObj.payload[`${key}-${DELTA_KEY}`],
                          )}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        gap={1}
                      >
                        <Typography
                          sx={{
                            color: payloadObj.color,
                            fontSize: '13px',
                            textShadow: chartTooltipShadow,
                          }}
                        >
                          10th pct
                        </Typography>
                        <Typography
                          sx={{
                            color: payloadObj.color,
                            fontSize: '13px',
                            fontWeight: 'bold',
                            textShadow: chartTooltipShadow,
                          }}
                        >
                          {formatAsPercentage(payloadObj.value[0])}
                        </Typography>
                      </Stack>
                      <Stack
                        key={key}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                        gap={1}
                      >
                        <Typography
                          sx={{
                            color: payloadObj.color,
                            fontSize: '13px',
                            textShadow: chartTooltipShadow,
                          }}
                        >
                          90th pct
                        </Typography>
                        <Typography
                          sx={{
                            color: payloadObj.color,
                            fontSize: '13px',
                            fontWeight: 'bold',
                            textShadow: chartTooltipShadow,
                          }}
                        >
                          {formatAsPercentage(payloadObj.value[1])}
                        </Typography>
                      </Stack>
                    </Stack>
                  );
                } else {
                  return (
                    <Stack
                      key={key}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Typography
                        sx={{
                          color: payloadObj.color,
                          fontSize: '13px',
                          textShadow: chartTooltipShadow,
                        }}
                      >
                        {payloadObj.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: payloadObj.color,
                          fontSize: '13px',
                          fontWeight: 'bold',
                          textShadow: chartTooltipShadow,
                        }}
                      >
                        {formatAsPercentage(payloadObj.value)}
                      </Typography>
                    </Stack>
                  );
                }
              })}
            </Stack>
          ))}
        </Paper>
      );
    }

    return null;
  };

  const SkewDiffTooltip = ({ active, payload }: any) => {
    if (
      !active ||
      !payload ||
      payload.length === 0 ||
      skewDiffComparison == null
    ) {
      return null;
    }

    const point = payload[0].payload as VolSkew;
    const xValue = point[volSkewSharedKey];
    const change = point[SKEW_DIFF_KEY];
    const baseValue = point[skewDiffComparison.baseLine.key];
    const comparisonValue = point[skewDiffComparison.comparisonLine.key];

    if (!Number.isFinite(change)) {
      return null;
    }

    return (
      <Paper
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '12px',
          gap: '6px',
          color: theme.palette.text.primary,
          border: 'none',
          backgroundColor: alpha(theme.palette.background.paper, 0.85),
          boxShadow: theme.palette.shadows.paperBoxShadow,
          width: '320px',
        }}
      >
        <Stack>{customTooltipTickFormatter(volSkewSharedKey, xValue)}</Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          gap={1}
        >
          <Typography sx={{ fontSize: '13px' }}>Skew Difference</Typography>
          <Typography
            sx={{
              color:
                change >= 0
                  ? theme.palette.success.main
                  : theme.palette.error.main,
              fontSize: '13px',
              fontWeight: 'bold',
            }}
          >
            {formatSkewDiff(change)}
          </Typography>
        </Stack>

        <Typography
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '11px',
            lineHeight: 1.25,
          }}
        >
          {skewDiffComparison.diffLabel}
        </Typography>

        {Number.isFinite(comparisonValue) && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap={1}
          >
            <Typography
              sx={{
                color: skewDiffComparison.comparisonLine.color,
                fontSize: '12px',
                textShadow: chartTooltipShadow,
              }}
            >
              {skewDiffComparison.comparisonLabel}
            </Typography>
            <Typography
              sx={{
                color: skewDiffComparison.comparisonLine.color,
                fontSize: '12px',
                fontWeight: 'bold',
                textShadow: chartTooltipShadow,
              }}
            >
              {formatAsPercentage(comparisonValue)}
            </Typography>
          </Stack>
        )}

        {Number.isFinite(baseValue) && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap={1}
          >
            <Typography
              sx={{
                color: skewDiffComparison.baseLine.color,
                fontSize: '12px',
                textShadow: chartTooltipShadow,
              }}
            >
              {skewDiffComparison.baseLabel}
            </Typography>
            <Typography
              sx={{
                color: skewDiffComparison.baseLine.color,
                fontSize: '12px',
                fontWeight: 'bold',
                textShadow: chartTooltipShadow,
              }}
            >
              {formatAsPercentage(baseValue)}
            </Typography>
          </Stack>
        )}
      </Paper>
    );
  };

  const SkewDiffBarShape = (props: any) => {
    const rawWidth = Number(props.width) || 0;
    const rawX = Number(props.x) || 0;
    const width =
      volSkewSharedKey === 'delta-x' ? Math.max(rawWidth, 2) : rawWidth;
    const x =
      volSkewSharedKey === 'delta-x' ? rawX - (width - rawWidth) / 2 : rawX;

    return <Rectangle {...props} x={x} width={width} />;
  };

  const RotatedVerticalLabel = ({
    viewBox,
    value,
    backgroundColor = theme.palette.background.paper,
    textColor = theme.palette.sgGreen,
    fontSize = 13,
    fontWeight = 500,
    topOffset = 130,
  }: any) => {
    const { x, width } = viewBox;

    // Adjust these values as needed for your chart's layout
    // How far down from the very top of the viewBox you want the label to
    // appear - callers stacking multiple labels near the same x should pass
    // distinct values so they don't render on top of one another.
    const padding = 10; // Padding around the text for the background
    const estimatedTextLength = value.length * (fontSize * 0.6); // Estimate based on average character width
    const rectHeight = fontSize + padding; // Height of the rectangle, adjusted for padding
    const rectWidth = estimatedTextLength + padding; // Width of the rectangle, adjusted for padding and estimated text length

    // Calculate the center X of the area available for the label
    const centerX = x + width / 2;

    // Adjusted Y position for top placement
    const adjustedY = topOffset + rectHeight / 2; // Adjust this to move the label lower or higher

    return (
      <g>
        {/* Background rectangle with adjusted positioning and size */}
        <rect
          x={centerX - rectWidth / 2} // Center the rectangle horizontally around the text
          y={adjustedY - rectHeight / 2} // Center the rectangle vertically around the text
          width={rectWidth}
          height={rectHeight}
          fill={backgroundColor}
          rx={5} // adds rounded corners
          transform={`rotate(-90, ${centerX}, ${adjustedY})`} // Rotates the text around its center
        />
        {/* Rotated text */}
        <text
          x={centerX}
          y={adjustedY}
          fill={textColor}
          fontSize={fontSize}
          fontWeight={fontWeight}
          letterSpacing={1}
          textAnchor="middle" // Ensures the text is horizontally centered
          dominantBaseline="central" // Ensures the text is vertically centered
          transform={`rotate(-90, ${centerX}, ${adjustedY})`} // Rotates the text around its center
        >
          {value}
        </text>
      </g>
    );
  };

  // Converts a raw strike price to this chart's current x-axis value.
  // Moneyness and delta-x aren't stored per-strike anywhere else, so they're
  // derived here the same way the current-price marker derives them below.
  const getMarkedStrikeX = (strike: number): number | null => {
    if (volSkewSharedKey === 'strike') {
      return strike;
    }
    if (volSkewSharedKey === 'moneyness') {
      return closingPrice != null && !isNaN(closingPrice) && closingPrice !== 0
        ? strike / closingPrice
        : null;
    }
    if (volSkewChartData == null || volSkewChartData.length === 0) {
      return null;
    }
    const idx = findNearestIdx(
      volSkewChartData.map((vs) => vs['strike']),
      strike,
    );
    return volSkewChartData[idx]?.['delta-x'] ?? null;
  };

  if (!selectedSym || loading) {
    return <Loader isLoading={loading} />;
  }

  if (volSkewData?.length === 0 && !loading && error != null) {
    return (
      <ErrorContent content="Failed to retrieve the data needed for Volatility Skew Chart. Please either refresh the page or contact us!" />
    );
  }

  return (
    <ChartWatermarkContainer
      ref={chartRef}
      style={{
        flex: 1,
        position: 'relative',
      }}
      size={20}
      offsetX={55}
      offsetY={50}
      sym={selectedSym}
      symStyles={{ right: '35px' }}
    >
      {!hasIVolAccess && isMobile && (
        <Stack alignItems="center">
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: 14,
            }}
          >
            Access limited to <strong>{selectedSym}</strong>
          </Typography>
        </Stack>
      )}

      <ResponsiveContainer
        width="100%"
        height={skewDiffComparison == null ? '100%' : '69%'}
      >
        <ComposedChart
          margin={{
            ...DEFAULT_CHART_MARGINS,
            bottom: skewDiffComparison == null ? (isMobile ? 40 : 20) : 5,
            right: 45,
          }}
          {...zoomChartConfig}
          data={displayedVolSkewChartData}
        >
          <CartesianGrid strokeDasharray="1 10" stroke={theme.palette.gray} />
          <XAxis
            allowDataOverflow
            label={
              skewDiffComparison == null
                ? {
                    value: selectedDisplayType,
                    fontSize: 12,
                    offset: 3,
                    position: 'insideBottom',
                    fontWeight: 600,
                  }
                : undefined
            }
            dataKey={volSkewSharedKey}
            domain={['dataMin', 'dataMax']}
            height={skewDiffComparison == null ? undefined : 10}
            tick={skewDiffComparison == null ? { fontSize: 12 } : false}
            type="number"
            ticks={ticks}
            tickFormatter={tickFormatter(volSkewSharedKey)}
          />
          {showZoomBrush && skewDiffComparison == null && (
            <Brush
              dataKey={volSkewSharedKey}
              tickFormatter={tickFormatter(volSkewSharedKey)}
              startIndex={activeBrushZoomConfig.leftIdx}
              endIndex={activeBrushZoomConfig.rightIdx}
              onChange={onBrushChange}
              height={25}
              travellerWidth={15}
              stroke={theme.palette.gray}
              fill={theme.palette.background.paper}
              alwaysShowText
            />
          )}
          <YAxis
            allowDataOverflow
            yAxisId="left"
            domain={[
              (dataMin: number) => Math.max(dataMin - 0.03, 0),
              (dataMax: number) => dataMax + 0.03,
            ]}
            tick={{ fontSize: 12 }}
            type="number"
            label={{
              ...DEFAULT_Y_AXIS_STYLES,
              value: 'Implied Volatility',
              offset: 0,
            }}
            tickFormatter={(value: number) => formatAsPercentage(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            wrapperStyle={{ fontSize: '12px' }}
            height={40}
          />

          {visibleSelectedDatedGreeks.map((datedGreeks: DatedGreeks) => {
            const name =
              datedGreeks.legendLabel ??
              getVolSkewLineLabel(
                datedGreeks.tradeDate,
                datedGreeks.expiryDate,
                currentTimezone,
              );
            const stroke = datedGreeks.color;
            return (
              <Area
                type="monotone"
                key={`area-${datedGreeks.key}`}
                yAxisId="left"
                legendType="none"
                name={`Bounds ${name}`}
                dataKey={`bounds-${datedGreeks.key}`}
                stroke="none"
                fillOpacity={0.3}
                fill={stroke}
                connectNulls
                hide={!showVolSkewIVStats}
              />
            );
          })}

          {visibleSelectedDatedGreeks.map((datedGreeks: DatedGreeks) => {
            const name =
              datedGreeks.legendLabel ??
              getVolSkewLineLabel(
                datedGreeks.tradeDate,
                datedGreeks.expiryDate,
                currentTimezone,
              );
            const stroke = datedGreeks.color;
            // Use the default current IV key for the main line
            const currentIVKey = datedGreeks.key;
            const adjustedKey = `${datedGreeks.key}-adjusted`;
            return (
              <Fragment key={`line-${datedGreeks.key}`}>
                <Line
                  key={currentIVKey}
                  yAxisId="left"
                  type="monotone"
                  dataKey={currentIVKey}
                  name={name}
                  stroke={stroke}
                  dot={false}
                  strokeWidth={3}
                  connectNulls
                />
                {ivAdjustmentParams && (
                  <Line
                    key={adjustedKey}
                    yAxisId="left"
                    type="monotone"
                    dataKey={adjustedKey}
                    name={`${name} (Adjusted)`}
                    stroke="#ff6b6b"
                    dot={false}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    opacity={0.8}
                    connectNulls
                  />
                )}
              </Fragment>
            );
          })}

          {/* Reference line for the selected reference line type */}
          {referenceLine &&
            visibleSelectedDatedGreeks.map((datedGreeks: DatedGreeks) => {
              const name =
                datedGreeks.legendLabel ??
                getVolSkewLineLabel(
                  datedGreeks.tradeDate,
                  datedGreeks.expiryDate,
                  currentTimezone,
                );
              const referenceLineKey = getReferenceLineKey(
                referenceLine,
                datedGreeks.key,
              );
              return (
                <Line
                  key={`reference-${referenceLineKey}`}
                  yAxisId="left"
                  type="monotone"
                  dataKey={referenceLineKey}
                  name={`${name} (${referenceLine.replace('_', ' ').toUpperCase()})`}
                  stroke={alpha(datedGreeks.color, 0.6)}
                  dot={false}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  connectNulls
                />
              );
            })}

          {(() => {
            // Stagger colliding labels by their actual rendered length, not a fixed offset.
            const estimateLabelLength = (text: string, fontSize = 13) =>
              text.length * (fontSize * 0.6) + 10;

            const markers: {
              key: string;
              x: number;
              label: string;
              lineColor: string;
              textColor: string;
              length: number;
            }[] = [];
            if (
              closingPrice != null &&
              volSkewSharedKey !== 'delta-x' &&
              !isNaN(closingPrice)
            ) {
              const priceX =
                volSkewSharedKey === 'strike'
                  ? closingPrice
                  : volSkewSharedKey === 'moneyness'
                    ? 1
                    : (volSkewChartData?.[
                        findNearestIdx(
                          volSkewChartData.map((vs) => vs['strike']),
                          closingPrice,
                        )
                      ]?.['delta-x'] ?? 0.5);
              const priceLabel = `Current Price: ${formatAsCurrency(closingPrice)}`;
              markers.push({
                key: 'current-price',
                x: priceX,
                label: priceLabel,
                lineColor: theme.palette.primary.main,
                textColor: theme.palette.sgGreen,
                length: estimateLabelLength(priceLabel),
              });
            }
            markedStrikeGroups.forEach(({ strike, label, color }) => {
              const x = getMarkedStrikeX(strike);
              if (x == null) {
                return;
              }
              const markerColor = color ?? theme.palette.warning.main;
              markers.push({
                key: `marked-strike-${strike}`,
                x,
                label,
                lineColor: markerColor,
                textColor: markerColor,
                length: estimateLabelLength(label),
              });
            });

            const xValues = (volSkewChartData ?? [])
              .map((vs) => vs[volSkewSharedKey])
              .filter((v): v is number => typeof v === 'number' && !isNaN(v));
            const domainSpan =
              xValues.length > 0
                ? Math.max(...xValues) - Math.min(...xValues)
                : 0;
            const collisionThreshold = domainSpan * 0.02;

            const BASE_OFFSET = 130;
            const GAP = 10;
            let prevX: number | null = null;
            let prevBottom = -Infinity;
            const positioned = [...markers]
              .sort((a, b) => a.x - b.x)
              .map((marker) => {
                const collides =
                  prevX != null &&
                  Math.abs(marker.x - prevX) < collisionThreshold;
                const topOffset = collides ? prevBottom + GAP : BASE_OFFSET;
                prevBottom = topOffset + marker.length;
                prevX = marker.x;
                return { ...marker, topOffset };
              });

            return positioned.map((marker) => (
              <ReferenceLine
                key={marker.key}
                x={marker.x}
                isFront
                stroke={marker.lineColor}
                ifOverflow="visible"
                yAxisId="left"
                style={{
                  opacity: 0.75,
                }}
                strokeDasharray="8 8"
                label={
                  <RotatedVerticalLabel
                    value={marker.label}
                    backgroundColor={theme.palette.background.default}
                    textColor={marker.textColor}
                    fontSize={13}
                    fontWeight={600}
                    topOffset={marker.topOffset}
                  />
                }
              />
            ));
          })()}

          {getZoomConfigRefArea(zoomConfig, 'left')}
        </ComposedChart>
      </ResponsiveContainer>

      {skewDiffComparison != null && (
        <ResponsiveContainer
          width="100%"
          height="31%"
          minHeight={isMobile ? 135 : 160}
        >
          <ComposedChart
            margin={{
              ...DEFAULT_CHART_MARGINS,
              bottom: isMobile ? 40 : 20,
              right: 45,
            }}
            {...zoomChartConfig}
            data={skewDiffComparison.data}
          >
            <CartesianGrid strokeDasharray="1 10" stroke={theme.palette.gray} />
            <XAxis
              allowDataOverflow
              label={{
                value: selectedDisplayType,
                fontSize: 12,
                offset: 3,
                position: 'insideBottom',
                fontWeight: 600,
              }}
              dataKey={volSkewSharedKey}
              domain={['dataMin', 'dataMax']}
              tick={{ fontSize: 12 }}
              type="number"
              ticks={ticks}
              tickFormatter={tickFormatter(volSkewSharedKey)}
            />
            {showZoomBrush && (
              <Brush
                dataKey={volSkewSharedKey}
                tickFormatter={tickFormatter(volSkewSharedKey)}
                startIndex={activeBrushZoomConfig.leftIdx}
                endIndex={activeBrushZoomConfig.rightIdx}
                onChange={onBrushChange}
                height={25}
                travellerWidth={15}
                stroke={theme.palette.gray}
                fill={theme.palette.background.paper}
                alwaysShowText
              />
            )}
            <YAxis
              allowDataOverflow
              yAxisId="skewDiff"
              domain={skewDiffComparison.domain}
              tick={{ fontSize: 12 }}
              type="number"
              label={{
                ...DEFAULT_Y_AXIS_STYLES,
                value: 'Skew Diff',
                offset: 0,
              }}
              tickFormatter={(value: number) =>
                value > 0 ? `+${value.toFixed(1)}` : value.toFixed(1)
              }
            />
            <Tooltip content={<SkewDiffTooltip />} />
            <ReferenceLine
              y={0}
              yAxisId="skewDiff"
              stroke={theme.palette.text.primary}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
            <Bar
              dataKey={SKEW_DIFF_KEY}
              name="Skew Difference"
              yAxisId="skewDiff"
              maxBarSize={10}
              isAnimationActive={false}
              shape={<SkewDiffBarShape />}
            >
              {skewDiffComparison.data.map((point) => {
                const change = point[SKEW_DIFF_KEY];
                const fill =
                  Number.isFinite(change) && change < 0
                    ? alpha(theme.palette.error.main, 0.75)
                    : alpha(theme.palette.success.main, 0.75);

                return (
                  <Cell
                    key={`skew-diff-${point[volSkewSharedKey]}`}
                    fill={fill}
                  />
                );
              })}
            </Bar>
            {getZoomConfigRefArea(zoomConfig, 'skewDiff')}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </ChartWatermarkContainer>
  );
};

export default VolSkewChart;
